import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {useBallot, useSaveDraft, useSaveResults} from '../../shared/api/useBallots';
import {useCurrentUser} from '../../shared/api/useCurrentUser';
import {useMusicTracks} from '../../shared/api/useMusic';
import {usePeople} from '../../shared/api/usePeople';
import type {BallotResource} from '../../shared/ballotTypes';
import {ErrorNotice} from '../../shared/components/notice';
import {putRequest} from '../../shared/utils/httpClient';
import {shouldFlush, shouldSchedule} from './autosave';
import type {ResultsValue} from './components/resultsSettings';
import {Editor, type SaveState} from './editor';
import {editorReducer, fromResource, toSaveBody} from './editorState';

const AUTOSAVE_MS = 600;

const EditorLoaded = ({
    ballot,
    directory,
}: {
    ballot: BallotResource;
    directory: ReadonlyMap<string, string>;
}) => {
    const [draft, dispatch] = useReducer(editorReducer, ballot, (b) => fromResource(b, directory));
    const [results, setResults] = useState<ResultsValue>({
        scope: ballot.results_scope,
        showCount: ballot.show_count,
        showShare: ballot.show_share,
        showVoters: ballot.show_voters,
    });
    const client = useQueryClient();
    const me = useCurrentUser();
    const tracks = useMusicTracks();
    const saveDraft = useSaveDraft(ballot.id);
    const saveResults = useSaveResults(ballot.id);
    const editable = ballot.status === 'draft';
    const body = toSaveBody(draft);
    const key = JSON.stringify(body);
    const [savedKey, setSavedKey] = useState(() =>
        JSON.stringify(toSaveBody(fromResource(ballot))),
    );
    const sent = useRef(savedKey);
    const failed = useRef<string | null>(null);
    const latest = useRef({body, key, editable});
    latest.current = {body, key, editable};

    useEffect(() => {
        if (!editable || !shouldSchedule(key, sent.current)) return;
        const timer = setTimeout(() => {
            sent.current = key;
            saveDraft.mutate(latest.current.body, {
                onSuccess: () => {
                    failed.current = null;
                    setSavedKey(key);
                },
                onError: () => {
                    failed.current = key;
                },
            });
        }, AUTOSAVE_MS);
        return () => clearTimeout(timer);
    }, [key, editable, saveDraft.mutate]);

    // Flush an edit still waiting on the debounce (or failed) when the editor closes.
    useEffect(() => {
        const flush = () => {
            const {body: pending, key: pendingKey, editable: canSave} = latest.current;
            if (!canSave || !shouldFlush(pendingKey, sent.current, failed.current)) return;
            sent.current = pendingKey;
            failed.current = null;
            putRequest(`/api/admin/ballots/${ballot.id}`, pending, {keepalive: true})
                .catch(() => undefined)
                .finally(() => client.invalidateQueries({queryKey: ['ballot', ballot.id]}));
        };
        window.addEventListener('pagehide', flush);
        return () => {
            window.removeEventListener('pagehide', flush);
            flush();
        };
    }, [ballot.id, client]);

    const onResults = (next: ResultsValue) => {
        setResults(next);
        saveResults.mutate({
            results_scope: next.scope,
            show_count: next.showCount,
            show_share: next.showShare,
            show_voters: next.showVoters,
        });
    };

    const unsaved = editable && key !== savedKey;
    const saveState: SaveState =
        saveDraft.isPending || saveResults.isPending
            ? 'saving'
            : (saveDraft.isError && unsaved) || saveResults.isError
              ? 'failed'
              : unsaved
                ? 'saving'
                : saveDraft.isSuccess || saveResults.isSuccess
                  ? 'saved'
                  : 'idle';

    return (
        <Editor
            ballot={ballot}
            draft={draft}
            dispatch={dispatch}
            results={results}
            onResults={onResults}
            tracks={tracks}
            saveState={saveState}
            slackAvailable={me.data?.integrations.slack ?? false}
            onPresent={() => window.open(`/present/${ballot.id}`, '_blank')}
        />
    );
};

export const EditorContainer = () => {
    const {id = ''} = useParams();
    const {data, error, isPending} = useBallot(id);
    const people = usePeople();
    // Option chips and title auto-fill need names, so wait for the directory (cached) first.
    const directory = useMemo(
        () => new Map((people.data?.people ?? []).map((p) => [p.humaans_id, p.name])),
        [people.data],
    );
    if (isPending || people.isPending)
        return (
            <p className="bt-help" role="status">
                Loading...
            </p>
        );
    if (error || !data) return <ErrorNotice title="Could not load this vote" error={error} />;
    return <EditorLoaded key={data.id} ballot={data} directory={directory} />;
};
